// BSEE DataTables helper — the declarative, JSON-fed table pattern (DataTables 3, no jQuery).
//
// A table opts in with one attribute and says where its rows come from:
//
//     <table class="table table-sm" data-bsee-table data-bsee-url="/api/v1/widgets">
//       <thead><tr>
//         <th data-bsee-field="name">Name</th>
//         <th data-bsee-field="active" data-bsee-render="checkmark">Live</th>
//       </tr></thead>
//     </table>
//
// The URL returns `{"data": [...rows]}` — or, with `data-bsee-server-side`, the DataTables
// server-side protocol (draw / recordsTotal / recordsFiltered / data; see DataTableQuery). Every
// option is derived from attributes so a page needs no inline <script> at all; anything the
// attributes cannot say goes in `data-bsee-options` as JSON, merged last.
//
// #grid reports are NOT handled here — BIZ owns those through ca/report/datatable_setup.js.

import DataTable, { type Config, type ColumnsConfig } from 'datatables.net-bs5';
import { bizTable } from './biz-table';

type Plain = Record<string, unknown>;
type RendererFactory = (options: Plain) => ColumnsConfig['render'];

const SELECTOR = '[data-bsee-table]';

/** Shared defaults for every BSEE table. Session-only state so a changed column set never resurrects stale state. */
const DEFAULTS: Config = {
    autoWidth: false,
    stateSave: true,
    stateDuration: -1,
    order: [],
    pageLength: 25,
    lengthMenu: [
        [10, 25, 100, -1],
        [10, 25, 100, 'All'],
    ],
    layout: {
        topStart: 'pageLength',
        topEnd: 'search',
        bottomStart: 'info',
        bottomEnd: 'paging',
    },
};

/** A server-side table holds one page, so 'All' (-1) would silently cap at the API's maximum. */
const SERVER_SIDE_LENGTHS = [
    [10, 25, 100],
    [10, 25, 100],
];

/** Plain-object deep merge — arrays and non-plain values are replaced, not merged. */
function merge<T extends object>(target: T, source: object): T {
    const out = target as Plain;
    for (const [key, value] of Object.entries(source)) {
        const current = out[key];
        out[key] = isPlain(value) && isPlain(current) ? merge({ ...current }, value) : value;
    }
    return target;
}

function isPlain(value: unknown): value is Plain {
    return typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype;
}

/** JSON from an attribute; a malformed value is reported rather than silently ignored. */
function jsonAttr(el: Element, name: string): Plain {
    const raw = el.getAttribute(name);
    if (!raw) {
        return {};
    }
    try {
        return JSON.parse(raw);
    } catch (err) {
        console.error(`bsee.datatable: ${name} is not valid JSON`, el, err);
        return {};
    }
}

/**
 * Column definitions from the header row. Only built when at least one <th> names a field —
 * a table fed by HTML rows has no `columns` to declare.
 */
function columnsFrom(table: HTMLTableElement): ColumnsConfig[] | null {
    const headerRows = table.querySelectorAll(':scope > thead > tr');
    const headerRow = headerRows[headerRows.length - 1];
    if (!headerRow) {
        return null;
    }

    const cells = Array.from(headerRow.querySelectorAll<HTMLTableCellElement>(':scope > th, :scope > td'));
    if (!cells.some((th) => th.hasAttribute('data-bsee-field'))) {
        return null;
    }

    return cells.map((th) => {
        const column: ColumnsConfig = { data: th.getAttribute('data-bsee-field') || null };
        const renderName = th.getAttribute('data-bsee-render');

        if (renderName) {
            const factory = (bizTable as Plain)[renderName];
            if (typeof factory === 'function' && renderName !== 'esc' && renderName !== 'safeHref') {
                column.render = (factory as RendererFactory)(jsonAttr(th, 'data-bsee-render-options'));
            } else {
                console.error(`bsee.datatable: unknown renderer "${renderName}"`, th);
            }
        }
        if (!column.render && column.data !== null) {
            // DataTables writes cell data as HTML. JSON rows are API data, not markup, so a column
            // without an explicit renderer is escaped — otherwise a stored `<img onerror=…>` runs.
            column.render = DataTable.render.text();
        }
        if (th.hasAttribute('data-bsee-no-sort')) {
            column.orderable = false;
        }
        if (column.data === null) {
            // An action/blank column: nothing to read, sort or search.
            column.defaultContent = '';
            column.orderable = false;
            column.searchable = false;
        }
        return column;
    });
}

/** Resolve a table's full DataTables options from its attributes, then `overrides`. */
function buildOptions(table: HTMLTableElement, overrides: Config = {}): Config {
    const options: Config = merge({}, DEFAULTS);

    // Any table that loads over ajax shows the loader while it waits — server-side or not.
    const url = table.getAttribute('data-bsee-url');
    if (url) {
        options.ajax = { url, dataSrc: 'data' };
        options.processing = true;
    }

    if (table.hasAttribute('data-bsee-server-side')) {
        options.serverSide = true;
        options.processing = true;
        options.lengthMenu = SERVER_SIDE_LENGTHS;
    }

    if (table.getAttribute('data-bsee-state') === 'off') {
        options.stateSave = false;
    }

    const pageLength = Number(table.getAttribute('data-bsee-page-length'));
    if (pageLength > 0) {
        options.pageLength = pageLength;
    }

    const columns = columnsFrom(table);
    if (columns) {
        options.columns = columns;
    }

    merge(options, jsonAttr(table, 'data-bsee-options'));
    return merge(options, overrides);
}

/** Initialise one table. Idempotent: a second call returns the existing instance. */
function init(table: HTMLTableElement, overrides: Config = {}) {
    if (DataTable.isDataTable(table)) {
        return new DataTable(table);
    }
    return new DataTable(table, buildOptions(table, overrides));
}

/** Initialise every opted-in table under `root`. Called on DOMContentLoaded by the bundle. */
function scan(root: ParentNode = document) {
    return Array.from(root.querySelectorAll<HTMLTableElement>(SELECTOR)).map((table) => init(table));
}

/**
 * Initialise #grid with the ColumnControl house set. Not auto-run — BIZ reports use
 * ca/report/datatable_setup.js — kept for non-BIZ projects that load this bundle.
 */
function initGrid() {
    const gridEl = document.querySelector<HTMLTableElement>('#grid');
    if (!gridEl) {
        return;
    }

    gridEl.classList.add('table', 'table-striped', 'table-hover', 'table-bordered');

    return init(gridEl, {
        columnControl: [
            {
                target: 0,
                content: [
                    'order',
                    ['search', 'searchList', 'spacer', 'orderAsc', 'orderDesc', 'orderAddAsc', 'orderAddDesc', 'orderClear'],
                ],
            },
        ],
        ordering: {
            indicators: false,
            handler: false,
        },
        stateDuration: 7200,
        fixedHeader: {
            header: true,
            footer: true,
            headerOffset: document.querySelector('.navbar')?.clientHeight || 0,
        },
        layout: {
            topStart: { buttons: ['pageLength', 'ccSearchClear'] },
        },
    });
}

export const datatable = {
    init,
    scan,
    buildOptions,
    initGrid,
    defaults: DEFAULTS,
};
